import {
  buildSuggestionParams,
  type SuggestionFilter,
} from './suggestion-params';
import {
  CONFIRM_BATCH_SIZE,
  getMappingSuggestions,
  postConfirm,
  postStatus,
  STATUS_BATCH_SIZE,
} from './suggestions-api';
import type {
  ConfirmDecision,
  ConfirmResponse,
  MappingSuggestionRow,
  RowOutcome,
  StatusAction,
} from './suggestion-types';

/** The most rows the server lists in one call. */
export const LIST_BATCH_SIZE = 200;

export function chunk<T>(items: T[], size: number): T[][] {
  const batches: T[][] = [];

  for (let start = 0; start < items.length; start += size) {
    batches.push(items.slice(start, start + size));
  }

  return batches;
}

/**
 * Every row of the To review queue in the High band, across all its pages
 * (under the connection and search filters on screen), for "Select all high
 * confidence".
 */
export async function fetchHighConfidence(
  filter: SuggestionFilter,
): Promise<MappingSuggestionRow[]> {
  const rows: MappingSuggestionRow[] = [];
  let page = 1;
  let totalPages = 1;

  while (page <= totalPages) {
    const params = buildSuggestionParams('to_review', filter, {
      band: 'high',
      page,
      limit: LIST_BATCH_SIZE,
    });
    const answer = await getMappingSuggestions(params);

    rows.push(...answer.data);
    totalPages = answer.total_pages;
    page += 1;
  }

  return rows;
}

/** What the row's suggestion says to link, or null when it has none. */
export const decisionOf = (
  row: MappingSuggestionRow,
): ConfirmDecision | null =>
  row.itemId
    ? {
        listingId: row.listingId,
        itemId: row.itemId,
        packQuantity: row.packQuantity,
      }
    : null;

export type BatchProgress = { done: number; total: number };

export type ConfirmRun = {
  /** One per decision, in the order sent. */
  outcomes: RowOutcome[];
  /** The failure that stopped the run, if any; the rows it left are `not_sent`. */
  error: unknown;
};

const unsent = (batch: ConfirmDecision[]): RowOutcome[] =>
  batch.map(({ listingId }) => ({ listingId, status: 'not_sent' }));

/**
 * Sends decisions one batch at a time, so a failure leaves the later batches
 * unsent instead of racing them. Each row comes back with its own outcome.
 */
export async function confirmInBatches(
  decisions: ConfirmDecision[],
  onProgress?: (progress: BatchProgress) => void,
  send: (batch: ConfirmDecision[]) => Promise<ConfirmResponse> = postConfirm,
): Promise<ConfirmRun> {
  const outcomes: RowOutcome[] = [];
  let error: unknown = null;

  for (const batch of chunk(decisions, CONFIRM_BATCH_SIZE)) {
    if (error) {
      outcomes.push(...unsent(batch));
      continue;
    }

    try {
      const { results } = await send(batch);
      const byListing = new Map(results.map(r => [r.listingId, r.status]));

      outcomes.push(
        ...batch.map(({ listingId }): RowOutcome => {
          const status = byListing.get(listingId);

          return { listingId, status: status ?? 'not_sent' };
        }),
      );
      onProgress?.({ done: outcomes.length, total: decisions.length });
    } catch (failure) {
      error = failure;
      outcomes.push(...unsent(batch));
    }
  }

  return { outcomes, error };
}

/** Skip or restore any number of listings, within the server's per-call limit. */
export async function changeStatusInBatches(
  action: StatusAction,
  listingIds: string[],
) {
  for (const batch of chunk(listingIds, STATUS_BATCH_SIZE)) {
    await postStatus(action, batch);
  }
}

export type OutcomeSummary = {
  linked: number;
  changed: number;
  gone: number;
  notSent: number;
};

export function summarize(outcomes: RowOutcome[]): OutcomeSummary {
  const count = (...statuses: RowOutcome['status'][]) =>
    outcomes.filter(({ status }) => statuses.includes(status)).length;

  return {
    linked: count('mapped', 'unchanged'),
    changed: count('changed_meanwhile'),
    gone: count('not_found'),
    notSent: count('not_sent'),
  };
}

/** "310 linked, 2 changed meanwhile - review them" */
export function resultLine({ linked, changed, gone, notSent }: OutcomeSummary) {
  const parts = [
    `${linked} linked`,
    changed ? `${changed} changed meanwhile` : '',
    gone ? `${gone} no longer exist` : '',
    notSent ? `${notSent} not sent` : '',
  ].filter(Boolean);
  const line = parts.join(', ');

  return parts.length > 1 ? `${line} - review them` : line;
}

export const OUTCOME_WORDS: Record<RowOutcome['status'], string> = {
  mapped: 'Linked',
  unchanged: 'Already linked to that product',
  changed_meanwhile: 'Changed meanwhile, review it',
  not_found: 'No longer exists',
  not_sent: 'Not sent, try again',
};
